/**
 * Auth Store - User authentication state management
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthState, AuthActions, SignUpData, LoginCredentials, Profile } from '@/types/auth';
import {
  signInWithEmail,
  signUpWithEmail,
  signOut as supabaseSignOut,
  resetPassword as supabaseResetPassword,
  getSession,
  fetchProfile,
  updateProfile as supabaseUpdateProfile,
  onAuthStateChange,
} from '@/services/supabase';

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      profile: null,
      session: null,
      isLoading: false,
      isAuthenticated: false,
      error: null,

      // Actions
      signIn: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null });

        const { data, error } = await signInWithEmail(credentials);

        if (error) {
          set({ isLoading: false, error: error.message });
          return { error: error.message };
        }

        if (data?.user) {
          const profile = await fetchProfile(data.user.id);
          set({
            user: data.user,
            session: data.session,
            profile,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        }

        return {};
      },

      signUp: async (data: SignUpData) => {
        set({ isLoading: true, error: null });

        const { data: authData, error } = await signUpWithEmail(data);

        if (error) {
          set({ isLoading: false, error: error.message });
          return { error: error.message };
        }

        if (authData?.user) {
          // User created but needs email verification
          set({
            user: authData.user,
            session: authData.session,
            isAuthenticated: !!authData.session,
            isLoading: false,
            error: null,
          });

          // Fetch profile if session exists
          if (authData.session) {
            const profile = await fetchProfile(authData.user.id);
            set({ profile });
          }
        }

        return {};
      },

      signOut: async () => {
        set({ isLoading: true });
        await supabaseSignOut();
        set({
          user: null,
          profile: null,
          session: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },

      resetPassword: async (email: string) => {
        set({ isLoading: true, error: null });

        const { error } = await supabaseResetPassword(email);

        set({ isLoading: false });

        if (error) {
          set({ error: error.message });
          return { error: error.message };
        }

        return {};
      },

      updateProfile: async (data: Partial<Profile>) => {
        const { user } = get();
        if (!user) {
          return { error: 'Not authenticated' };
        }

        set({ isLoading: true, error: null });

        const result = await supabaseUpdateProfile(user.id, data);

        if (result.error) {
          set({ isLoading: false, error: result.error });
          return result;
        }

        // Refresh profile
        const profile = await fetchProfile(user.id);
        set({ profile, isLoading: false });

        return {};
      },

      refreshSession: async () => {
        // Don't set isLoading=true — refresh silently in background
        // so returning users aren't blocked by a loading spinner
        try {
          const { data, error } = await getSession();

          if (error) {
            console.error('Session refresh error:', error);
            set({
              user: null,
              profile: null,
              session: null,
              isAuthenticated: false,
              isLoading: false,
            });
            return;
          }

          if (data?.session?.user) {
            const profile = await fetchProfile(data.session.user.id);
            set({
              user: data.session.user,
              session: data.session,
              profile,
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            set({
              user: null,
              profile: null,
              session: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } catch (error) {
          console.error('Session refresh failed:', error);
          set({
            user: null,
            profile: null,
            session: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      fetchProfile: async () => {
        const { user } = get();
        if (!user) return;

        const profile = await fetchProfile(user.id);
        set({ profile });
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'blurp-auth-store',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        // Persist essential user data so checkout works instantly without refreshSession()
        user: state.user ? { id: (state.user as any).id, email: (state.user as any).email } : null,
        profile: state.profile ? { id: state.profile.id, email: state.profile.email } : null,
      }),
    }
  )
);

// Initialize auth state on app load
export function initializeAuth() {
  const store = useAuthStore.getState();

  // Check for existing session
  store.refreshSession();

  // Listen for auth changes
  onAuthStateChange(async (event, session) => {
    if (event === 'SIGNED_IN' && session) {
      const typedSession = session as { user: { id: string } };
      const profile = await fetchProfile(typedSession.user.id);
      useAuthStore.setState({
        user: typedSession.user as AuthState['user'],
        session: session as AuthState['session'],
        profile,
        isAuthenticated: true,
        isLoading: false,
      });
    } else if (event === 'SIGNED_OUT') {
      useAuthStore.setState({
        user: null,
        profile: null,
        session: null,
        isAuthenticated: false,
        isLoading: false,
      });
    } else if (event === 'TOKEN_REFRESHED' && session) {
      useAuthStore.setState({
        session: session as AuthState['session'],
      });
    }
  });
}

// Selectors
export const selectUser = (state: AuthStore) => state.user;
export const selectProfile = (state: AuthStore) => state.profile;
export const selectIsAuthenticated = (state: AuthStore) => state.isAuthenticated;
export const selectIsLoading = (state: AuthStore) => state.isLoading;
export const selectAuthError = (state: AuthStore) => state.error;
